import {
  AsyncSelectView,
  AsyncSelectViewProps,
  MultiAsyncSelectView,
  MultiAsyncSelectViewProps,
  useAsyncData,
  useAsyncSelectState,
  useMultiAsyncData,
  useMultiAsyncSelectState,
} from '@porsche-code/kado-ui';
import { SelectChangeEventDetail } from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import {
  OrganizationQuery,
  OrganizationsQuery,
  OrganizationsQueryVariables,
  useOrganizationQuery,
  useOrganizationsQuery,
} from '@/graphql';

type Data =
  | NonNullable<OrganizationsQuery['organizations']['docs'][number]>
  | NonNullable<OrganizationQuery['organization']>;
type Filter = NonNullable<OrganizationsQueryVariables['filter']>;

export const OrganizationSelect = ({
  dataFilter,
  groupBy,
  ...props
}: Omit<AsyncSelectViewProps, 'data' | 'selectState' | 'i18n' | 'onChange'> & {
  dataFilter?: Filter;
  groupBy?: (d: Data) => string;
  onChange?: (
    e: CustomEvent<SelectChangeEventDetail>,
    organization?: Data | null,
  ) => void;
}) => {
  const { t } = useTranslation(NAMESPACES);

  const queryClient = useQueryClient();

  const { enabled, searchString, selectState } = useAsyncSelectState(
    props.autoSelectSingleOption,
  );

  const selectData = useData({
    enabled,
    filter: dataFilter,
    searchString,
    value:
      props.value === null || props.value === undefined
        ? undefined
        : String(props.value),
    groupByFn: groupBy,
    appendixFn: appendix,
  });

  return (
    <AsyncSelectView
      {...props}
      data={selectData}
      selectState={selectState}
      i18n={{
        noData: t('common:noData'),
        error: t('common:error'),
      }}
      onChange={(e) => {
        const { value } = e.target as HTMLElement & { value: string };

        const organization = value
          ? (queryClient.getQueryData<OrganizationQuery>(
              useOrganizationQuery.getKey({ id: Number(value) }),
            )?.organization ??
            queryClient
              .getQueriesData<OrganizationsQuery>({
                exact: false,
                queryKey: useOrganizationsQuery.getKey(),
              })
              .flatMap(([, data]) => data?.organizations.docs ?? [])
              .find((opt) => opt.id.toString() === value))
          : null;

        props.onChange?.(e, organization);
      }}
    />
  );
};

const useData = ({
  enabled,
  filter,
  searchString,
  value,
  groupByFn,
  appendixFn,
}: {
  enabled?: boolean;
  filter?: Filter;
  searchString?: string;
  value?: string;
  groupByFn?: (d: Data) => string;
  appendixFn?: (d: Data) => string;
}) => {
  const {
    data: organizations,
    isPending,
    isLoading,
    error: organizationsError,
  } = useOrganizationsQuery(
    {
      filter,
      searchString,
    },
    {
      enabled,
      select: (data) => data.organizations.docs,
    },
  );

  const { data: organization, error: organizationError } = useOrganizationQuery(
    {
      id: Number(value),
    },
    {
      enabled: Boolean(value),
      initialData: value
        ? {
            organization: organizations?.find(
              (opt) => opt.id.toString() === value,
            ),
          }
        : undefined,
      select: (data) => data.organization,
    },
  );

  const { options, optgroups } = useAsyncData({
    data: organizations,
    selected: organization,
    searchString,
    equalsFn: (a, b) => a.id === b.id,
    toOptionFn: (d) => ({ label: d.name, value: d.id.toString() }),
    groupByFn,
    appendixFn,
  });

  return {
    options,
    optgroups,
    selectedOption: organization
      ? {
          label: organization.name,
          value: organization.id.toString(),
        }
      : undefined,

    isPending,
    isLoading,
    error: organizationsError ?? organizationError,
  };
};

export const MultiOrganizationSelect = ({
  dataFilter,
  groupBy,
  ...props
}: Omit<MultiAsyncSelectViewProps, 'data' | 'selectState' | 'i18n'> & {
  dataFilter?: Filter;
  groupBy?: (d: Data) => string;
}) => {
  const { t } = useTranslation(NAMESPACES);

  const { enabled, searchString, selectState } = useMultiAsyncSelectState();

  const selectData = useMultiData({
    enabled,
    filter: dataFilter,
    searchString,
    value:
      props.value === null || props.value === undefined
        ? undefined
        : props.value.map((v) => String(v)),
    groupByFn: groupBy,
    appendixFn: appendix,
  });

  return (
    <MultiAsyncSelectView
      {...props}
      data={selectData}
      selectState={selectState}
      i18n={{
        noData: t('common:noData'),
        error: t('common:error'),
      }}
    />
  );
};

const appendix = (d: Data) =>
  'partnerNumber' in d && d.partnerNumber ? `${d.partnerNumber}` : '';

const useMultiData = ({
  enabled,
  filter,
  searchString,
  value,
  groupByFn,
  appendixFn,
}: {
  enabled?: boolean;
  filter?: Filter;
  searchString?: string;
  value?: string[];
  groupByFn?: (d: Data) => string;
  appendixFn?: (d: Data) => string;
}) => {
  const {
    data: organizations,
    isPending,
    isLoading,
    error: organizationsError,
  } = useOrganizationsQuery(
    {
      filter,
      searchString,
    },
    {
      enabled,
      select: (data) => data.organizations.docs,
    },
  );

  const { data: organizationsSelected, error: organizationError } =
    useOrganizationsQuery(
      {
        filter: value ? { _id: { _in: value.map(Number) } } : undefined,
      },
      {
        enabled: Boolean(value),
        initialData: value
          ? {
              organizations: {
                docs:
                  organizations?.filter((opt) =>
                    value.includes(opt.id.toString()),
                  ) ?? [],
              },
            }
          : undefined,
        select: (data) => data.organizations.docs,
      },
    );

  const { options, optgroups } = useMultiAsyncData({
    data: organizations,
    selected: organizationsSelected,
    searchString,
    equalsFn: (a, b) => a.id === b.id,
    toOptionFn: (d) => ({ label: d.name, value: d.id.toString() }),
    groupByFn,
    appendixFn,
  });

  return {
    options,
    optgroups,
    selectedOptions: organizationsSelected
      ? organizationsSelected.map((organization) => ({
          label: organization.name,
          value: organization.id.toString(),
        }))
      : undefined,

    isPending,
    isLoading,
    error: organizationsError ?? organizationError,
  };
};
